import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AdaptationQuestionFields from "../adaptation/AdaptationQuestionFields";
import { formatQuestionAnswer } from "../adaptation/answerFormat";
import { normalizeQuestionnaire } from "../adaptation/catalog";
import { isQuestionVisible } from "../adaptation/showIf";
import { rememberAdaptationTake } from "../adaptation/takeLinks";
import { formatDateRu } from "../adaptation/rules";
import { getPublicAdaptationForm, submitPublicAdaptationForm } from "../services/adaptationApi";

const pageBackground = "min-h-screen bg-[radial-gradient(circle_at_top_right,_rgba(251,191,36,0.16),_transparent_30%),linear-gradient(to_bottom,_#f8fafc,_#eef2f7)] px-4 py-8 text-slate-900 md:py-12";

function FormProgress({ step, dark = false }) {
    return <ol className="grid grid-cols-3 gap-2" aria-label="Этапы заполнения формы">
        {["Ответы", "Проверка", "Готово"].map((label, index) => {
            const number = index + 1;
            const active = step >= number;
            return <li key={label} className="text-center">
                <div className={`mx-auto grid h-9 w-9 place-items-center rounded-full text-sm font-bold ${active ? "bg-amber-400 text-slate-950" : dark ? "bg-white/10 text-slate-400" : "bg-slate-100 text-slate-400"}`}>{active && step > number ? "✓" : number}</div>
                <p className={`mt-1 text-xs font-semibold ${active ? (dark ? "text-white" : "text-slate-800") : "text-slate-400"}`}>{label}</p>
            </li>;
        })}
    </ol>;
}

export default function AdaptationPublicFormPage() {
    const { token } = useParams();
    const [form, setForm] = useState(null);
    const [values, setValues] = useState({});
    const [state, setState] = useState("loading");
    const [message, setMessage] = useState("");

    useEffect(() => {
        let active = true;
        setState("loading");
        setForm(null);
        setValues({});
        setMessage("");
        getPublicAdaptationForm(token)
            .then((data) => {
                if (!active) return;
                const normalized = {
                    ...data,
                    // [Claude] 2026-11-02 — анкеты этапов из конструктора приходят готовыми, без подмены каталогом
                    questions: data.builder ? (data.questions || []) : normalizeQuestionnaire(data.kind, data.role, data.questions),
                };
                setForm(normalized);
                rememberAdaptationTake({ ...normalized, token });
                setState(data.locked ? "done" : "ready");
            })
            .catch((error) => {
                if (!active) return;
                setMessage(error.message || "Ссылка недействительна");
                setState("error");
            });
        return () => { active = false; };
    }, [token]);

    const submit = async (event) => {
        event.preventDefault();
        if (state === "ready") {
            const missing = (form.questions || []).find((question) => {
                if (question.required === false) return false;
                if (!isQuestionVisible(question, values, form.core_history || {})) return false;
                const value = values[question.id];
                return value == null
                    || (typeof value === "string" && value.trim() === "")
                    || (Array.isArray(value) && value.length === 0);
            });
            if (missing) {
                setMessage(`Заполните обязательное поле: «${missing.text}»`);
                return;
            }
            setMessage("");
            setState("review");
            return;
        }
        setState("saving");
        try {
            await submitPublicAdaptationForm(token, values);
            rememberAdaptationTake({ ...form, token, submitted: true });
            setState("done");
            setMessage("Спасибо! Ответы сохранены и переданы HR.");
        } catch (error) {
            setState("ready");
            setMessage(error.message || "Не удалось отправить ответы");
        }
    };

    if (state === "loading") {
        return (
            <main className={`${pageBackground} grid place-items-center`}>
                <div className="text-center"><span className="mx-auto block h-10 w-10 animate-pulse rounded-2xl bg-amber-300" /><p className="mt-4 font-medium text-slate-600">Загружаем вашу форму…</p></div>
            </main>
        );
    }
    if (state === "error") {
        return (
            <main className={`${pageBackground} grid place-items-center`}>
                <div className="w-full max-w-md rounded-3xl border border-red-100 bg-white p-8 text-center shadow-xl shadow-slate-200/60">
                    <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-red-50 text-2xl text-red-600">!</span>
                    <h1 className="mt-5 text-2xl font-bold text-slate-900">Ссылка недоступна</h1>
                    <p className="mt-3 text-sm leading-6 text-red-700">{message}</p>
                    <p className="mt-5 text-xs leading-5 text-slate-500">Попросите HR-специалиста прислать новую персональную ссылку.</p>
                </div>
            </main>
        );
    }
    if (state === "done") {
        return (
            <main className={`${pageBackground} grid place-items-center`}>
                <div className="w-full max-w-lg rounded-[28px] border border-emerald-100 bg-white p-8 text-center shadow-xl shadow-slate-200/60 md:p-10">
                    <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-100 text-3xl font-bold text-emerald-700">✓</span>
                    <p className="mt-6 text-xs font-bold tracking-[0.2em] text-amber-700">АДАПТАЦИЯ</p>
                    <h1 className="mt-2 text-3xl font-bold text-slate-900">Форма заполнена</h1>
                    <p className="mt-3 leading-7 text-slate-600">{message || "Ответы уже были отправлены."}</p>
                    <div className="mt-7"><FormProgress step={3} /></div>
                    <p className="mt-7 rounded-2xl bg-slate-50 p-4 text-sm text-slate-500">Можно закрыть эту страницу. Повторно отправлять форму не нужно.</p>
                </div>
            </main>
        );
    }

    if (state === "review") {
        return (
            <main className={pageBackground}>
                <form onSubmit={submit} className="mx-auto max-w-3xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-xl shadow-slate-200/60">
                    <div className="border-b border-slate-100 bg-slate-950 px-6 py-7 text-white md:px-9"><FormProgress step={2} dark /><h1 className="mt-7 text-3xl font-bold">Проверьте ответы перед отправкой</h1><p className="mt-2 text-sm leading-6 text-slate-300">
                        После подтверждения ответы сохранятся в карточке сотрудника и будут заблокированы.
                    </p></div>
                    <div className="space-y-6 p-6 md:p-9"><dl className="divide-y divide-slate-100 rounded-2xl border border-slate-100 px-5">
                        {(form.questions || [])
                            .filter((question) => isQuestionVisible(question, values, form.core_history || {}) && values[question.id] != null && values[question.id] !== "")
                            .map((question) => (
                                <div key={question.id} className="py-4">
                                    <dt className="text-sm leading-5 text-slate-500">{question.text}</dt>
                                    <dd className="mt-1 text-base font-semibold text-slate-900">{formatQuestionAnswer(question, values[question.id])}</dd>
                                </div>
                            ))}
                    </dl>
                    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                        <button type="button" onClick={() => setState("ready")} className="min-h-12 rounded-2xl border border-slate-300 px-6 py-3 font-semibold text-slate-700 transition hover:bg-slate-50">Изменить ответы</button>
                        <button className="min-h-12 rounded-2xl bg-amber-400 px-6 py-3 font-bold text-slate-950 shadow-lg shadow-amber-100 transition hover:bg-amber-300">Подтвердить и отправить</button>
                    </div></div>
                </form>
            </main>
        );
    }

    return (
        <main className={pageBackground}>
            <form onSubmit={submit} className="mx-auto max-w-3xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-xl shadow-slate-200/60">
                <header className="bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 px-6 py-8 text-white md:px-9 md:py-10">
                    <FormProgress step={1} dark />
                    <p className="mt-8 text-xs font-bold tracking-[0.2em] text-amber-300">АДАПТАЦИЯ СОТРУДНИКА</p>
                    <h1 className="mt-2 text-3xl font-bold tracking-tight md:text-4xl">{form.title}</h1>
                    <p className="mt-3 text-sm leading-6 text-slate-300">
                        {form.full_name ? `${form.full_name} · ` : ""}
                        {form.kind_label || ""}
                        {form.plan_date ? ` · до ${formatDateRu(form.plan_date)}` : ""}
                    </p>
                </header>
                <div className="space-y-6 p-6 md:p-9">
                    <div className="rounded-2xl border border-sky-100 bg-sky-50 p-4 text-sm leading-6 text-sky-900"><b className="block">Перед отправкой можно всё проверить</b>Отвечайте спокойно и честно. Черновик остаётся на этой странице до подтверждения.</div>
                    {form.late_answer ? (
                        <p className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
                            Ответ будет сохранён как поздний и добавлен в историю версии.
                        </p>
                    ) : null}
                <AdaptationQuestionFields
                    questions={form.questions || []}
                    values={values}
                    onChange={(id, value) => setValues((current) => ({ ...current, [id]: value }))}
                    coreHistory={form.core_history || {}}
                    disabled={state === "saving"}
                />
                {message ? <p role="alert" className="rounded-2xl border border-red-100 bg-red-50 p-4 text-sm font-medium text-red-700">{message}</p> : null}
                <div className="flex justify-end border-t border-slate-100 pt-6"><button disabled={state === "saving"} className="min-h-12 w-full rounded-2xl bg-slate-950 px-6 py-3 text-base font-bold text-white shadow-lg transition hover:bg-slate-800 disabled:opacity-50 sm:w-auto">
                    {state === "saving" ? "Отправка…" : "Отправить ответы"}
                </button></div>
                </div>
            </form>
        </main>
    );
}
